/**
 * Authentication Cookie Helpers for Sub-Websites
 * 
 * This file provides utilities to read authentication data from cookies
 * set by the main website. Works across localhost subdomains.
 * 
 * Cookies set by main website (localhost:3000):
 * - access_token: JWT authentication token
 * - user_data: User profile information (JSON)
 */

/**
 * Get access token from cookie or localStorage (fallback for IP-based deployments)
 * @returns {string|null} - Access token or null if not found
 */
export const getAccessToken = () => {
  try {
    // Try cookie first
    const match = document.cookie
      .split('; ')
      .find(row => row.startsWith('access_token='));
    
    if (match) {
      const token = match.split('=')[1];
      if (token) {
        const decoded = decodeURIComponent(token);
        // Sync cookie to localStorage
        localStorage.setItem('access_token', decoded);
        return decoded;
      }
    }
    
    // IMPORTANT: If no cookie but localStorage exists = stale data
    // Clear it to stay in sync with main website sign-out
    const stored = localStorage.getItem('access_token');
    if (stored && !match) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      return null;
    }
    
    return stored || null;
  } catch (error) {
    console.error('[AuthCookieHelpers] Error reading access token:', error);
    return null;
  }
};

/**
 * Get user data from cookie or localStorage (fallback for IP-based deployments)
 * @returns {Object|null} - User data object or null if not found
 */
export const getUserData = () => {
  try {
    // Try cookie first
    const match = document.cookie
      .split('; ')
      .find(row => row.startsWith('user_data='));
    
    if (match) {
      const encoded = match.split('=')[1];
      if (encoded) {
        const decoded = decodeURIComponent(encoded);
        const userData = JSON.parse(decoded);
        // Sync cookie to localStorage
        localStorage.setItem('user_data', decoded);
        return userData;
      }
    }
    
    // IMPORTANT: If no cookie but localStorage exists = stale data
    const stored = localStorage.getItem('user_data');
    if (stored && !match) {
      localStorage.removeItem('user_data');
      return null;
    }
    
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('[AuthCookieHelpers] Error reading user data:', error);
    return null;
  }
};

/**
 * Check if user is authenticated
 * @returns {boolean} - True if user has valid token
 */
export const isAuthenticated = () => {
  const token = getAccessToken();
  return token !== null && token !== '';
};

/**
 * Get complete auth data
 * @returns {Object} - Auth state object
 */
export const getAuthData = () => {
  const token = getAccessToken();
  const user = getUserData();
  
  // User is authenticated if either:
  // 1. They have a valid token, OR
  // 2. They have valid user data (sufficient for display/UI purposes)
  const authenticated = (token !== null && token !== '') || (user !== null);
  
  return {
    token,
    user,
    isAuthenticated: authenticated,
    isLoading: false
  };
};

/**
 * Get Authorization headers for API requests
 * @returns {Object} - Headers object with Authorization
 */
export const getAuthHeaders = () => {
  const token = getAccessToken();
  
  if (!token) {
    return {
      'Content-Type': 'application/json'
    };
  }
  
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

/**
 * Authenticated fetch wrapper
 * @param {string} url - API endpoint URL
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} - Fetch response
 */
export const authenticatedFetch = async (url, options = {}) => {
  const headers = getAuthHeaders();
  
  return fetch(url, {
    ...options,
    headers: {
      ...headers,
      ...options.headers
    },
    credentials: 'include' // Include cookies
  });
};

/**
 * Redirect to auth page if not authenticated
 * @param {string} authUrl - Main website URL
 */
export const redirectToAuthIfNeeded = (authUrl = 'http://localhost:3000') => {
  if (!isAuthenticated()) {
    const returnUrl = encodeURIComponent(window.location.href);
    window.location.href = `${authUrl}/signin?returnUrl=${returnUrl}`;
  }
};

/**
 * Extract authentication from URL parameters and store in localStorage
 * This is a workaround for IP-based deployments where cookies don't work across ports
 * @returns {boolean} - True if auth data was extracted from URL
 */
export const extractAuthFromUrl = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const userDataParam = urlParams.get('user');
    
    if (token && userDataParam) {
      // Store in localStorage
      localStorage.setItem('access_token', token);
      localStorage.setItem('user_data', decodeURIComponent(userDataParam));
      
      // Clean URL by removing auth parameters
      urlParams.delete('token');
      urlParams.delete('user');
      
      const cleanUrl = window.location.pathname + 
        (urlParams.toString() ? '?' + urlParams.toString() : '') +
        window.location.hash;
      
      window.history.replaceState({}, document.title, cleanUrl);
      
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('[AuthCookieHelpers] Error extracting auth from URL:', error);
    return false;
  }
};

/**
 * Clear authentication data from both cookies and localStorage
 */
export const clearAuthData = () => {
  try {
    // Clear localStorage (both our keys and main website's keys for sync)
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('token'); // Main website uses 'token'
    
    // Clear cookies with proper domain attributes
    const hostname = window.location.hostname;
    let domainAttr = '';
    
    if (hostname === 'localhost') {
      domainAttr = 'Domain=localhost; ';
    } else if (hostname.includes('.')) {
      // Extract root domain (e.g., tadreamk.com from bizcard.tadreamk.com)
      const parts = hostname.split('.');
      if (parts.length >= 2) {
        const rootDomain = `.${parts.slice(-2).join('.')}`;
        domainAttr = `Domain=${rootDomain}; `;
      }
    }
    
    // Clear cookies with domain attribute
    document.cookie = `access_token=; ${domainAttr}Path=/; Max-Age=0; SameSite=Lax`;
    document.cookie = `user_data=; ${domainAttr}Path=/; Max-Age=0; SameSite=Lax`;
    
    // Also clear without domain attribute (in case some exist without it)
    document.cookie = 'access_token=; Path=/; Max-Age=0; SameSite=Lax';
    document.cookie = 'user_data=; Path=/; Max-Age=0; SameSite=Lax';
  } catch (error) {
    console.error('[AuthCookieHelpers] Error clearing auth data:', error);
  }
};

const authCookieHelpers = {
  getAccessToken,
  getUserData,
  isAuthenticated,
  getAuthData,
  getAuthHeaders,
  authenticatedFetch,
  redirectToAuthIfNeeded,
  extractAuthFromUrl,
  clearAuthData
};

export default authCookieHelpers;



